import { ForwardedRef, forwardRef, useCallback } from 'react';

import { useControlledState } from '../../../../hooks';
import { EyeIcon, NoEyeIcon } from '../../../../icons';
import {
  DefaultComponentProps,
  ElementSize,
  RenderPropFC,
} from '../../../../types';
import { cn, renderFCRenderProp } from '../../../../utils';
import { InputClassNames } from '../../constants';
import InputMainWrapper from '../InputMainWrapper/InputMainWrapper';

import { InputElement, TogglePasswordButton } from './InputBox.styled';

export interface InputBoxProps
  extends React.HTMLAttributes<HTMLDivElement>,
    DefaultComponentProps {
  elementSize: ElementSize;
  hasError: boolean | undefined;
  leading?: RenderPropFC;
  trailing?: RenderPropFC;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  /** In case you want to access inner `<input/>` element you can use this prop */
  inputRef?: ForwardedRef<HTMLInputElement>;
  hasNoDisabledStyle?: boolean;
}

const InputBox = forwardRef<HTMLDivElement, InputBoxProps>((props, rootRef) => {
  const {
    className,
    elementSize,
    'data-testid': dataTestId,
    hasError = false,
    leading,
    trailing,
    inputProps,
    inputRef,
    hasNoDisabledStyle = false,
    ...rest
  } = props;

  const {
    disabled,
    type = 'text',
    className: inputClassName,
    ...restInputProps
  } = inputProps || {};

  const [localInputType, setLocalInputType] = useControlledState(type);

  const toggleInputType = useCallback(() => {
    setLocalInputType(prevType => (prevType === 'text' ? 'password' : 'text'));
  }, [setLocalInputType]);

  return (
    <InputMainWrapper
      className={className}
      data-testid={dataTestId}
      disabled={!!disabled}
      elementSize={elementSize}
      hasError={hasError}
      hasNoDisabledStyle={hasNoDisabledStyle}
      leading={leading}
      ref={rootRef}
      trailing={
        <>
          {trailing && renderFCRenderProp(trailing, {})}
          {type === 'password' && !disabled && (
            <TogglePasswordButton
              aria-label="Toggle password visibility"
              className={InputClassNames.PasswordToggle}
              type="button"
              onClick={toggleInputType}>
              {localInputType === 'text' ? <NoEyeIcon /> : <EyeIcon />}
            </TogglePasswordButton>
          )}
        </>
      }
      {...rest}>
      <InputElement
        $size={elementSize}
        aria-invalid={hasError}
        className={cn(InputClassNames.Main, inputClassName)}
        disabled={disabled}
        ref={inputRef}
        {...restInputProps}
        type={localInputType}
      />
    </InputMainWrapper>
  );
});

InputBox.displayName = 'InputBox';

export default InputBox;
